import { find, flatMap } from "lodash";
import config from "../config";

export function getAttrLink(slug) {
  return `/${config.attrBase}/${slug}`;
}

export function isAttrHasChildren(attr) {
  return attr.children !== undefined;
}

export function isChildAttr(attr) {
  return attr.parent !== undefined;
}

export function isAttrOptionHasChildren(option) {
  return option.children !== undefined;
}

export function isAttrOptionHasCondition(option) {
  return option.conditions !== undefined;
}

export function getFlattenAttrOptions(options, children = false) {
  return flatMap(options, (option) => {
    let group = isAttrOptionHasCondition(option) ? option.items : [option];
    return flatMap(group, (opt) => {
      return isAttrOptionHasChildren(opt) && children ? opt.children : opt;
    });
  });
}

export function getFlattenAttrs(attrs) {
  return flatMap(attrs, (attr) => {
    return isAttrHasChildren(attr) ? [attr, ...attr.children] : attr;
  });
}

export function normalizeAttrsParams(attrs, params, touchedAttributes = []) {
  let result = Object.assign({}, params);

  getFlattenAttrs(attrs).forEach((attribute) => {
    if (result[attribute.slug] === undefined) {
      return;
    }

    let attrValue = getParamValue(params[attribute.slug]),
      isDefault = touchedAttributes.indexOf(attribute.slug) === -1,
      currentName = "",
      currentOption = undefined,
      defaultOption = undefined;

    const resultOpt = getOptionByValue(attribute, attrValue);
    if (resultOpt) {
      currentName = resultOpt.name;
    } else {
      console.log("missed attr value", attribute, attrValue);
    }

    attribute.options.some((option) => {
      let group = false;

      if (isAttrOptionHasCondition(option)) {
        if (checkAttrOptionCondition(option.conditions, result, attrs)) {
          group = option.items;
        }
      } else {
        group = [option];
      }

      if (group) {
        let groupDefaultOpt = undefined;

        group.some((option) => {
          const children = isAttrOptionHasChildren(option)
            ? option.children
            : [option];
          currentOption = find(children, { value: attrValue });

          if (defaultOption === undefined && !isDefault) {
            defaultOption = find(children, { name: currentName });
          }

          if (groupDefaultOpt === undefined) {
            groupDefaultOpt =  children[0];
          }

          let overrideGroupDefaultOpt = children.find((child) => child.isDefault === true);

          if(overrideGroupDefaultOpt && overrideGroupDefaultOpt !== groupDefaultOpt) {
            groupDefaultOpt = overrideGroupDefaultOpt;
          }

          return currentOption;
        });

        if (defaultOption === undefined) {
          defaultOption = groupDefaultOpt;
        }
      }

      return currentOption;
    });

    const resultOption = currentOption ? currentOption : defaultOption;

    if (!resultOption) {
      console.warn(attribute, params, resultOption);
      return;
    }

    result[attribute.slug] = {
      value: resultOption.value,
      price: resultOption.price,
      imgSlug: resultOption.imgSlug,
      productionNumber: resultOption.productionNumber,
    };

    if (isChildAttr(attribute)) {
      result[attribute.slug]["parentAttr"] = attribute.parent;
    }
  });

  return result;
}

export function getAttrsDefaultOptions(attrs) {
  let params = {};

  getFlattenAttrs(attrs).forEach((attr) => {
    let value = undefined;

    attr.options.some((option) => {
      let defaultOpt = false;

      if (isAttrOptionHasCondition(option)) {
        if (checkAttrOptionCondition(option.conditions, params, attrs)) {
          defaultOpt = option.items[0];
        }
      } else {
        defaultOpt = option;
      }

      if (defaultOpt !== false) {
        value = isAttrOptionHasChildren(defaultOpt)
          ? defaultOpt.children[0]
          : defaultOpt;
      }

      return value;
    });

    params[attr.slug] = value;
  });

  return params;
}

export function getSummary(attrs, params) {
  let summary = {};

  getFlattenAttrs(attrs).forEach((attr) => {
    let value = params[attr.slug].value,
      result = false;

    if (isChildAttr(attr) && params[attr.parent].price === 0) {
      return;
    }

    getFlattenAttrOptions(attr.options).some((option) => {
      if (isAttrOptionHasChildren(option)) {
        const child = find(option.children, { value });
        if (child) {
          result = {
            name: attr.name,
            slug: attr.slug,
            value: child.value,
            valueName: option.name,
            subValueName: child.name,
          };
        }
      } else if (option.value === value) {
        result = {
          name: attr.name,
          slug: attr.slug,
          value: option.value,
          valueName: option.name,
        };
      }

      if (result !== false) {
        result["_valueName"] = result.valueName;

        if (attr.slug === "frames") {
          result._valueName += ` (${option.productionNumber})`;
        }
      }

      return result;
    });

    if (isChildAttr(attr)) {
      summary[attr.parent]["additional"] =
        summary[attr.parent]["additional"] === undefined
          ? [result]
          : summary[attr.parent]["additional"].push(result);
    } else {
      summary[attr.slug] = result;
    }
  });

  return Object.values(summary);
}

export function getAttrOptionsByParams(attrs, params) {
  let result = {};

  getFlattenAttrs(attrs).forEach((attr) => {
    let value = params[attr.slug].value,
      selectedOption = false;

    getFlattenAttrOptions(attr.options).some((option) => {
      if (isAttrOptionHasChildren(option)) {
        const child = find(option.children, { value });
        if (child) {
          selectedOption = child;
        }
      } else if (option.value === value) {
        selectedOption = option;
      }
      return selectedOption;
    });

    result[attr.slug] = selectedOption;
  });

  return result;
}

export function getOptionsDeliveryTime(selectedOptions) {
  const deliveryTimes = Object.values(selectedOptions)
    .map((opt) => (opt.deliveryTime !== undefined ? opt.deliveryTime : false))
    .filter((val) => val)
    .sort(({ value: a }, { value: b }) => b - a);

  return deliveryTimes.length > 0 ? deliveryTimes[0] : false;
}

export function prepareParamsToConditions(attrs, params) {
  let result = {};

  attrs.forEach(({ slug }) => {
    let condValue = undefined;

    if (params[slug] === undefined) {
      result[slug] = condValue;
      return;
    }

    const currentValue = getParamValue(params[slug]),
      attr = find(attrs, { slug: slug }),
      options = getFlattenAttrOptions(attr.options, false);

    options.some((option) => {
      if (isAttrOptionHasChildren(option)) {
        const opt = find(option.children, { value: currentValue });
        if (opt) {
          condValue = slug === "tops" ? opt.name : option.name;
        }
      } else if (option.value === currentValue) {
        condValue = option.name;
      }
      return condValue;
    });

    result[slug] = condValue;
  });

  return result;
}

export function checkAttrOptionCondition(conditions, params, attrs, level = 1) {
  const comparators = {
    "=": (a, b) => a == b,
    ">": (a, b) => a > b,
    "<": (a, b) => a < b,
  };

  if (level === 1) {
    params = prepareParamsToConditions(attrs, params);
  }

  let results = [];

  conditions.forEach((group) => {
    let groupResults = [];

    if (Array.isArray(group[1])) {
      groupResults.push(
        checkAttrOptionCondition(group, params, attrs, ++level)
      );
    } else {
      group.forEach((condition) => {
        if (params[condition.key] !== undefined) {
          groupResults.push(
            comparators[condition.compare](
              params[condition.key],
              condition.value
            )
          );
        }
      });
    }

    results.push(groupResults.some((item) => item === true));
  });

  return results.every((item) => item === true);
}

export function getParamValue(param) {
  return typeof param === "object" ? param.value : param;
}

function getOptionByValue(attribute, value) {
  return find(getFlattenAttrOptions(attribute.options, true), { value });
}
